import { useEffect, useRef, useState, useCallback, memo } from 'react';
import tokenimg from '../assets/bitcoin.webp';

// ─── Static config (outside component — never recreated) ─────────────────────
const TOKEN_CONFIG = [
    { name: 'Bitcoin',  symbol: 'BTC', id: 'bitcoin'  },
    { name: 'Ethereum', symbol: 'ETH', id: 'ethereum' },
    { name: 'Cardano',  symbol: 'ADA', id: 'cardano'  },
    { name: 'Ripple',   symbol: 'XRP', id: 'ripple'   },
    { name: 'Litecoin', symbol: 'LTC', id: 'litecoin' },
    { name: 'Polkadot', symbol: 'DOT', id: 'polkadot' },
];

const COINGECKO_URL =
    'https://api.coingecko.com/api/v3/simple/price' +
    '?ids=' + TOKEN_CONFIG.map((t) => t.id).join(',') +
    '&vs_currencies=usd&include_24hr_change=true';

// ─── Pure helpers (no closure deps) ──────────────────────────────────────────
function formatPrice(usd) {
    if (usd >= 1000) return '$' + usd.toLocaleString('en-US', { maximumFractionDigits: 0 });
    if (usd >= 1)    return '$' + usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return '$' + usd.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
}

function deriveTokens(prices) {
    return TOKEN_CONFIG.map((t) => {
        const live     = prices[t.id];
        const price    = live ? formatPrice(live.usd) : '—';
        const raw      = live?.usd_24h_change ?? null;
        const change   = raw != null ? (raw >= 0 ? '+' : '') + raw.toFixed(2) + '%' : '—';
        const positive = raw == null || raw >= 0;
        return { ...t, price, change, positive };
    });
}

// ─── Memoised card — only re-renders when its own props change ────────────────
const TokenCard = memo(({ name, symbol, price, change, positive, loading }) => (
    <div className="group bg-white/[0.03] border border-white/[0.08] rounded-2xl p-3 sm:p-4 hover:border-violet-500/40 hover:bg-white/5 transition-all duration-300 cursor-pointer">
        <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-600 p-0.5 flex-shrink-0">
                    <div className="w-full h-full rounded-2xl bg-[#0a0a1a] flex items-center justify-center overflow-hidden">
                        <img
                            src={tokenimg}
                            alt={name}
                            loading="lazy"
                            decoding="async"
                            width={28}
                            height={28}
                            className="w-6 h-6 sm:w-7 sm:h-7 object-contain"
                        />
                    </div>
                </div>
                <div className="min-w-0">
                    <p className="text-white font-bold text-sm sm:text-base truncate">{name}</p>
                    <p className="text-gray-500 text-xs sm:text-sm">{symbol}</p>
                </div>
            </div>

            <div className="text-right flex-shrink-0 min-w-[72px]">
                {loading ? (
                    <>
                        <div className="h-4 w-20 bg-white/10 rounded animate-pulse mb-1.5" />
                        <div className="h-3 w-12 bg-white/10 rounded animate-pulse ml-auto" />
                    </>
                ) : (
                    <>
                        <p className="text-white font-bold text-sm sm:text-base">{price}</p>
                        <p className={`text-xs sm:text-sm font-semibold ${positive ? 'text-green-400' : 'text-red-400'}`}>
                            {change}
                        </p>
                    </>
                )}
            </div>
        </div>
    </div>
));
TokenCard.displayName = 'TokenCard';

// ─── Main component ───────────────────────────────────────────────────────────
function Tokens() {
    const [prices, setPrices]           = useState({});
    const [status, setStatus]           = useState('loading'); // 'loading' | 'ok' | 'error'
    const [lastUpdated, setLastUpdated] = useState('');
    const abortRef = useRef(null);

    const fetchPrices = useCallback(async () => {
        abortRef.current?.abort();
        abortRef.current = new AbortController();
        try {
            const res  = await fetch(COINGECKO_URL, { signal: abortRef.current.signal });
            if (!res.ok) throw new Error('bad response');
            const data = await res.json();
            setPrices(data);
            setLastUpdated(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
            setStatus('ok');
        } catch (err) {
            if (err.name !== 'AbortError') setStatus('error');
        }
    }, []);

    useEffect(() => {
        fetchPrices();
    }, []);

    const tokens  = deriveTokens(prices);
    const loading = status === 'loading';

    return (
        <section className="bg-[#0a0a1a] py-16 relative overflow-hidden">
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-64 bg-violet-900/20 blur-3xl rounded-full" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
                {/* Header */}
                <div className="text-center mb-10 sm:mb-16">
                    <span className="inline-block text-violet-400 text-xs sm:text-sm font-bold tracking-widest uppercase mb-3 sm:mb-4">
                        Markets
                    </span>
                    <h2 className="text-2xl sm:text-4xl lg:text-6xl font-black text-white mb-3 sm:mb-4">
                        Available{' '}
                        <span className="bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">
                            Tokens
                        </span>
                    </h2>
                    <p className="text-gray-500 text-sm sm:text-lg">
                        Trade top cryptocurrencies with real-time pricing
                    </p>

                    {/* Status badge */}
                    <div className="mt-4 inline-flex items-center gap-2 h-5">
                        {loading && <span className="text-gray-500 text-xs">Fetching prices…</span>}
                    
                        {status === 'ok' && (
                            <>
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inset-0 rounded-full bg-green-400 opacity-75" />
                                    <span className="relative rounded-full h-2 w-2 bg-green-400" />
                                </span>
                                <span className="text-gray-500 text-xs">Live · CoinGecko · {lastUpdated}</span>
                                <button onClick={fetchPrices} title="Refresh now" className="text-gray-600 hover:text-violet-400 transition-colors text-sm">↻</button>
                            </>
                        )}
                    </div>
                </div>

                {/* Token Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {tokens.map((token) => (
                        <TokenCard key={token.id} {...token} loading={loading} />
                    ))}
                </div>

                {/* CTA */}
                <div className="text-center mt-10 sm:mt-12">
                    <a href="#" className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 font-semibold transition-colors text-sm sm:text-base">
                        View all markets <span>→</span>
                    </a>
                </div>
            </div>
        </section>
    );
}

export default Tokens;